import { useCallback, useMemo } from 'react';

import { isCommunityAdmin, isCommunityMember } from 'common/utils';
import { selectMyCommunityId, useGetCurrentCommunityMemberQuery } from 'community/api';
import { UserPrivilegeDetails, UserPrivilegeName } from 'domain/UserProfile';
import { useAppSelector } from 'store';
import { selectUserAccount } from 'user/api';

export const useAuthorization = () => {
  const myCommunityId = useAppSelector(selectMyCommunityId);
  const account = useAppSelector(selectUserAccount);
  const { data, isLoading, isError } = useGetCurrentCommunityMemberQuery(
    { communityId: myCommunityId },
    { skip: !myCommunityId }
  );
  const { communityRole, privileges: userPrivileges } = data ?? {};

  const isActivityAllowed = useMemo<Record<UserPrivilegeName, boolean>>(
    () =>
      Object.values(UserPrivilegeDetails).reduce(
        (allPrivileges, { id, name }) => {
          allPrivileges[name] = !!userPrivileges?.some((up) => up.id === id);
          return allPrivileges;
        },
        {} as Record<UserPrivilegeName, boolean>
      ),
    [userPrivileges]
  );

  const hasPrivilege = useCallback(
    (privilegeNames: UserPrivilegeName[]) => privilegeNames?.some((p) => !!userPrivileges?.some((ap) => ap.name === p)),
    [userPrivileges]
  );

  return {
    communityMember: data,
    isError,
    isLoading,
    isCommunityAdmin: isCommunityAdmin({ communityRole }),
    isCommunityMember: isCommunityMember({ communityRole }),
    isWithoutCommunity: !myCommunityId,
    isOrganizationMember: !!account?.isOrganizationMember,
    isCompanyAdmin: !!account?.isCompanyAdmin,
    hasPrivilege,
    isActivityAllowed,
  };
};
